import type { Key } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { FileMenuActionEnum } from '../../enums/FileMenuActionEnum';
import type File from '../../interfaces/File';
import type FileActionContext from '../../interfaces/FileActionContext';
import {
  type FileActionKey,
  filteredFileActions,
  isFileActionAvailable,
} from '../../lib/fileActions';
import { useKeyboardShortcutsContext } from '../../contexts/KeyboardShortcutsContext';
import useOpenFile from '../useOpenFile';

interface UseFilesKeyboardNavigationParams {
  enabled: boolean;
  files: File[];
  selectedTableKeys: Key[];
  setSelectedTableKeys: (keys: Key[]) => void;
  handleFileMenuAction: (fileIds: number[], action: FileActionKey) => void;
  isGridMode: boolean;
  onToggleGridList: () => void;
  fileActionContext: FileActionContext;
}

/**
 * Keyboard navigation and selection for files table / grid views.
 */
export function useFilesKeyboardNavigation({
  enabled,
  files,
  selectedTableKeys,
  setSelectedTableKeys,
  handleFileMenuAction,
  isGridMode,
  onToggleGridList,
  fileActionContext,
}: UseFilesKeyboardNavigationParams) {
  const location = useLocation();
  const { openFileSafely } = useOpenFile();
  const { registerFilesNavigation, registerFileMenuAction, registerHandler, unregisterHandler } =
    useKeyboardShortcutsContext();

  const [focusedFileKey, setFocusedFileKey] = useState<number | null>(null);
  const filesRef = useRef(files);
  filesRef.current = files;

  const focusedIndex = useMemo(() => {
    if (focusedFileKey == null) return -1;
    return files.findIndex((file) => file.id === focusedFileKey);
  }, [files, focusedFileKey]);

  const selectedFiles = useMemo(
    () => files.filter((file) => selectedTableKeys.includes(file.id)),
    [files, selectedTableKeys],
  );

  const getFocusIndex = useCallback((): number => {
    if (focusedIndex >= 0) return focusedIndex;
    if (selectedTableKeys.length > 0) {
      const firstSelected = files.findIndex((file) => selectedTableKeys.includes(file.id));
      if (firstSelected >= 0) return firstSelected;
    }
    return files.length > 0 ? 0 : -1;
  }, [files, focusedIndex, selectedTableKeys]);

  const scrollFocusedIntoView = useCallback((fileId: number) => {
    const row =
      document.querySelector(`tr[data-row-key="${fileId}"]`) ??
      document.querySelector(`[data-file-card-id="${fileId}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, []);

  const onArrow = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right', extend: boolean) => {
      const list = filesRef.current;
      if (list.length === 0) return;

      const columns = isGridMode ? 4 : 1;
      const currentIndex = getFocusIndex();
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;
      if (direction === 'up') nextIndex -= columns;
      if (direction === 'down') nextIndex += columns;
      if (direction === 'left') nextIndex -= 1;
      if (direction === 'right') nextIndex += 1;

      nextIndex = Math.max(0, Math.min(list.length - 1, nextIndex));
      const nextFile = list[nextIndex];
      if (!nextFile) return;

      setFocusedFileKey(nextFile.id);
      scrollFocusedIntoView(nextFile.id);

      if (extend) {
        const selectedSet = new Set(selectedTableKeys);
        selectedSet.add(nextFile.id);
        setSelectedTableKeys([...selectedSet]);
      }
    },
    [getFocusIndex, isGridMode, scrollFocusedIntoView, selectedTableKeys, setSelectedTableKeys],
  );

  const onMoveFocus = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right') => {
      onArrow(direction, false);
    },
    [onArrow],
  );

  const onToggleSelection = useCallback(() => {
    const index = getFocusIndex();
    const list = filesRef.current;
    const file = list[index];
    if (!file) return;

    const isSelected = selectedTableKeys.includes(file.id);
    if (isSelected) {
      setSelectedTableKeys(selectedTableKeys.filter((key) => key !== file.id));
    } else {
      setSelectedTableKeys([...selectedTableKeys, file.id]);
    }
  }, [getFocusIndex, selectedTableKeys, setSelectedTableKeys]);

  const onSelectAll = useCallback(() => {
    setSelectedTableKeys(filesRef.current.map((file) => file.id));
  }, [setSelectedTableKeys]);

  const onClearSelection = useCallback(() => {
    setSelectedTableKeys([]);
    setFocusedFileKey(null);
  }, [setSelectedTableKeys]);

  const onFocusLandmark = useCallback((landmark: 'sidebar' | 'main' | 'details' | 'upload') => {
    if (landmark === 'sidebar') {
      document.querySelector<HTMLElement>('nav[aria-label] a')?.focus();
      return;
    }
    if (landmark === 'main') {
      document.getElementById('main-content')?.focus();
      return;
    }
    if (landmark === 'details') {
      document.querySelector<HTMLElement>('aside[aria-label] button')?.focus();
      return;
    }
    if (landmark === 'upload') {
      document.querySelector<HTMLElement>('[data-testid="upload-progress-drawer-toggle"]')?.click();
    }
  }, []);

  const runFileAction = useCallback(
    (action: FileMenuActionEnum) => {
      const ids =
        selectedFiles.length > 0
          ? selectedFiles.map((file) => file.id)
          : focusedFileKey != null
            ? [focusedFileKey]
            : [];
      if (ids.length === 0) return;
      const targetFiles = files.filter((file) => ids.includes(file.id));
      const available = filteredFileActions({ ...fileActionContext, files: targetFiles }, false);
      if (!isFileActionAvailable(available, action)) return;
      handleFileMenuAction(ids, action);
    },
    [fileActionContext, files, focusedFileKey, handleFileMenuAction, selectedFiles],
  );

  const handleOpenNewTab = useCallback(() => {
    const file =
      selectedFiles[0] ??
      (focusedFileKey != null ? files.find((item) => item.id === focusedFileKey) : undefined);
    if (!file || file.isFolder) return;
    openFileSafely(file, true);
  }, [files, focusedFileKey, openFileSafely, selectedFiles]);

  const navigationHandlers = useMemo(
    () => ({
      onArrow,
      onClearSelection,
      onFocusLandmark,
      onMoveFocus,
      onSelectAll,
      onToggleGridList,
      onToggleSelection,
    }),
    [
      onArrow,
      onClearSelection,
      onFocusLandmark,
      onMoveFocus,
      onSelectAll,
      onToggleGridList,
      onToggleSelection,
    ],
  );

  useEffect(() => {
    if (!enabled || !location.pathname.startsWith('/files')) {
      registerFilesNavigation(null);
      registerFileMenuAction(null);
      return;
    }

    registerFilesNavigation(navigationHandlers);
    registerFileMenuAction(runFileAction);

    return () => {
      registerFilesNavigation(null);
      registerFileMenuAction(null);
    };
  }, [
    enabled,
    location.pathname,
    navigationHandlers,
    registerFileMenuAction,
    registerFilesNavigation,
    runFileAction,
  ]);

  useEffect(() => {
    if (!enabled) return;
    registerHandler('file.openNewTab', handleOpenNewTab);
    return () => unregisterHandler('file.openNewTab', handleOpenNewTab);
  }, [enabled, handleOpenNewTab, registerHandler, unregisterHandler]);

  return {
    focusedFileKey,
    setFocusedFileKey,
  };
}
